/* eslint-disable no-shadow */
/* eslint-disable no-use-before-define */
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';

import PlayerStandingsCardV3 from '../../../../../components/cards/PlayerStandingsCard/PlayerStandingsCardV3';
import StandingsHeaderColumns from './StandingsHeaderColumns';
import InfoButton from '../../../../../components/InfoButton';
import TeamStandingsCardV3 from '../../../../../components/cards/TeamStandingsCard/TeamStandingsCardV3';

import { getDrawAllStats } from '../../../../../actions/event-profile-actions';
import { drawEliminationTypeIds, sportTypeIds } from '../../../../../utils/eventConstants';
import { isProDrawOrProDrawQualifier, rankList } from '../../../../../utils/events';
import { hasRetiredTag } from '../../../../../utils/types';
import { memberDetailToStandingsPlayer, hasParticipatingMembers } from '../../../../../utils/standingsPlayerMapper';
import { drawShape, eventShape, drawTagTypeShape } from '../../../../../propTypes';

function AllStandingsV3(props) {
    const {
        allStandings,
        allStandingsLoading,
        sportTypeId,
        draw,
        event,
        isPttEvent,
        drawTagTypes,
    } = props;

    const dispatch = useDispatch();

    const mainDrawStandingsData = allStandings?.mainDrawStats && (allStandings.mainDrawStats.playerStats || allStandings.mainDrawStats.teamStats);
    const childDrawStandingsData = allStandings?.childDrawStats && (allStandings.childDrawStats.playerStats || allStandings.childDrawStats.teamStats);
    const isPickleballEvent = sportTypeId === sportTypeIds.PICKLEBALL;
    const isRoundRobin = draw.eliminationTypeId === drawEliminationTypeIds.ROUND_ROBIN && draw.hasPlayoff;
    const isAdHoc = draw.eliminationTypeId === drawEliminationTypeIds.AD_HOC && draw.hasPlayoff;
    const isDoubleElimination = draw.eliminationTypeId === drawEliminationTypeIds.DOUBLE_ELIMINATION;
    const isFeedInBackDraw = draw.eliminationTypeId === drawEliminationTypeIds.FEED_IN_BACK_DRAW;
    const isFirstMatchBackDraw = draw.eliminationTypeId === drawEliminationTypeIds.FIRST_MATCH_BACKDRAW;
    const isSingleElimination = draw.eliminationTypeId === drawEliminationTypeIds.SINGLE_ELIMINATION;
    const isProDraw = isProDrawOrProDrawQualifier(draw.categoryTypeId);

    const refresh = () => {
        dispatch(getDrawAllStats(event.id, draw.id));
    };

    const rankOrderDeterminationSequence = () => {
        const message = [];
        if (isPickleballEvent) {
            if (isSingleElimination) {
                message.push('Finals / 3-4 Playoff matches');
                message.push('Total main draw wins');
                message.push('Point differential of all main draw games played');
            } else if (isDoubleElimination) {
                message.push('Medal matches (positions #1-3)');
                message.push('Total main draw wins');
                message.push('Total back draw wins');
                message.push('Point differential of all main draw games played');
            } else if (isProDraw) {
                message.push('Medal matches (positions #1-4)');
                message.push('Total main draw wins amongst players / teams who did not compete in the consolation draw');
                message.push('Total back draw wins amongst players / teams who competed in the consolation draw');
                message.push('Point differential of all main draw games played');
            } else {
                message.push('Greatest number of matches won');
                message.push('Head-to-head matches won among the tied players/teams');
                message.push('Point differential of all games played');
                message.push('Head-to-head point differential');
                message.push('Point differential against the next-highest player/team');
            }
        } else if (isPttEvent) {
            message.push('Greatest Number of Wins');
            message.push('Greatest Number of Matches Played');
            message.push('Head-to-Head (if only 2 players are tied)');
            message.push(`If 3+ players tied:`);
            message.push([
                `Players with fewer Matches Played are eliminated`,
                `Head-to-head when 2 players remain`,
                `If more than 1 player still remains`,
                [
                    `% Sets Won`,
                    `% Games Won`,
                    `UTR`,
                    `Coin Toss (random)`,
                ],
            ]);
        } else {
            message.push('Greatest Number of Wins');
            message.push('Greatest Number of Matches Played');
            message.push('Head to Head result if only two players are tied');
            message.push(`% of sets won`);
            message.push(`% of games won`);
            message.push('Overall Current Rating');
        }
        return message;
    };

    const shouldShowForfeitText = useMemo(() => {
        if (allStandings?.mainDrawStats?.playerStats || allStandings?.mainDrawStats?.teamStats) {
            return mainDrawStandingsData
                .filter(hasParticipatingMembers)
                .filter(standing => (hasRetiredTag(standing?.tagId) && standing?.stats !== null) || !!standing.outcome)?.length > 0;
        }

        return false;
    }, [allStandings, mainDrawStandingsData]);

    if (allStandingsLoading) {
        return <div data-testid="all-standings.loading-msg">Loading Standings...</div>;
    }
    if (!(mainDrawStandingsData || childDrawStandingsData)) {
        return (
            <div className="d-flex flex-row jcsb aic">
                <div data-testid="all-standings.no-stats-msg">No Stats For Draw</div>
                <div className="text-right">
                    <div className="ml16 btn btn-sm btn-default" onClick={refresh} data-testid="all-standings.refresh-btn-no-data">Refresh</div>
                </div>
            </div>
        );
    }

    return (
        <div className={``}>
            <div className="text-right" data-testid="all-standings.rank-order-header">How is rank order determined?
                <InfoButton
                    alignRight={true}
                    hoverText={(
                        <>
                            Rank order for standings is determined by
                            {rankList(rankOrderDeterminationSequence())}
                            {isPickleballEvent && (isRoundRobin || isAdHoc) && ('Note: Players/teams that have withdrawn from the bracket are not eligible to be considered when going to a tie-breaker and shall only have their record of matches won considered.')}
                        </>
                    )}
                />
                <div className="ml16 btn btn-sm btn-default" onClick={refresh} data-testid="all-standings.refresh-btn">Refresh</div>
            </div>
            <StandingsHeaderColumns
                sportTypeId={sportTypeId}
                isIndexDisplayed={!!(allStandings?.mainDrawStats?.playerStats || allStandings?.mainDrawStats?.teamStats)}
                isRoundRobin={isRoundRobin && childDrawStandingsData}
                isAdHoc={isAdHoc && childDrawStandingsData}
                isDoubleElimination={isDoubleElimination}
                isFeedInBackDraw={isFeedInBackDraw}
                isFirstMatchBackDraw={isFirstMatchBackDraw}
            />
            {allStandings?.mainDrawStats?.playerStats && mainDrawStandingsData
                .filter(hasParticipatingMembers)
                .map((standing, index) => {
                    const childDrawStanding = childDrawStandingsData ? childDrawStandingsData.find(cdStanding => standing.id === cdStanding.id) : null;
                    return (
                        <PlayerStandingsCardV3
                            key={standing.id}
                            drawMedal={standing.drawMedal}
                            divisionMedal={standing.divisionMedal}
                            divisionName={standing.divisionName}
                            player={memberDetailToStandingsPlayer(standing.participatingMembers[0])}
                            stats={standing.stats}
                            childDrawStats={childDrawStanding?.stats}
                            sportTypeId={sportTypeId}
                            index={index + 1}
                            isRoundRobin={isRoundRobin && childDrawStandingsData}
                            isAdHoc={isAdHoc && childDrawStandingsData}
                            isDoubleElimination={isDoubleElimination}
                            isFeedInBackDraw={isFeedInBackDraw}
                            isFirstMatchBackDraw={isFirstMatchBackDraw}
                            standingsPosition={standing.standingsPosition}
                            isIndexDisplayed={!!(allStandings?.mainDrawStats?.playerStats || allStandings?.mainDrawStats?.teamStats)}
                            outcome={standing?.outcome}
                            tagId={standing?.tagId}
                            isPttEvent={isPttEvent}
                            drawTagTypes={drawTagTypes}
                        />
                    );
                })}
            {allStandings?.mainDrawStats?.teamStats && mainDrawStandingsData
                .filter(hasParticipatingMembers)
                .map((standing, index) => {
                    const teamPlayers = (standing.participatingMembers ?? []).map(memberDetailToStandingsPlayer);
                    const childDrawStanding = childDrawStandingsData ? childDrawStandingsData.find(cdStanding => standing.teamId === cdStanding.teamId) : null;

                    return (
                        <TeamStandingsCardV3
                            index={index + 1}
                            key={standing.teamId}
                            drawMedal={standing.drawMedal}
                            divisionMedal={standing.divisionMedal}
                            teamPlayers={teamPlayers}
                            stats={standing.stats}
                            childDrawStats={childDrawStanding?.stats}
                            sportTypeId={sportTypeId}
                            isRoundRobin={isRoundRobin && childDrawStandingsData}
                            isAdHoc={isAdHoc && childDrawStandingsData}
                            isDoubleElimination={isDoubleElimination}
                            isFeedInBackDraw={isFeedInBackDraw}
                            isFirstMatchBackDraw={isFirstMatchBackDraw}
                            standingsPosition={standing.standingsPosition}
                            isIndexDisplayed={!!(allStandings?.mainDrawStats?.playerStats || allStandings?.mainDrawStats?.teamStats)}
                            outcome={standing?.outcome}
                            tagId={standing?.tagId}
                            isPttEvent={isPttEvent}
                            drawTagTypes={drawTagTypes}
                        />
                    );
                })}
            {shouldShowForfeitText && (
                <div className="mt16 pl24 pr24 text-center text-small text-black" data-testid="all-standings.forfeit-text">
                    Withdrawn, retired or forfeited players will be excluded from the standings calculation, but match results will still count for their opponents who remain in the event.
                </div>
            )}
        </div>
    );
}

AllStandingsV3.propTypes = {
    allStandings: PropTypes.shape({
        mainDrawStats: PropTypes.shape({
            playerStats: PropTypes.arrayOf(PropTypes.shape({
                memberId: PropTypes.number,
                position: PropTypes.number,
                wins: PropTypes.number,
                losses: PropTypes.number,
            })),
            teamStats: PropTypes.arrayOf(PropTypes.shape({
                teamId: PropTypes.number,
                memberIds: PropTypes.arrayOf(PropTypes.number),
                wins: PropTypes.number,
                losses: PropTypes.number,
            })),
        }),
        childDrawStats: PropTypes.shape({
            playerStats: PropTypes.arrayOf(PropTypes.shape({
                memberId: PropTypes.number,
                position: PropTypes.number,
                wins: PropTypes.number,
                losses: PropTypes.number,
            })),
            teamStats: PropTypes.arrayOf(PropTypes.shape({
                teamId: PropTypes.number,
                memberIds: PropTypes.arrayOf(PropTypes.number),
                wins: PropTypes.number,
                losses: PropTypes.number,
            })),
        }),
    }),
    allStandingsLoading: PropTypes.bool,
    sportTypeId: PropTypes.number,
    draw: drawShape,
    event: eventShape,
    drawTagTypes: PropTypes.arrayOf(drawTagTypeShape).isRequired,
};

AllStandingsV3.defaultProps = {
    allStandings: null,
    allStandingsLoading: false,
    sportTypeId: sportTypeIds.TENNIS,
    draw: {},
    event: {},
};

export default AllStandingsV3;
